import { useEffect, useState } from "react";

import {
  Close,
  CompareArrows,
  Delete,
  OpenInNew,
  Summarize,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  IntelImageMatchModel,
  SubmissionPortal,
  deleteIntelImageMatch,
  getSubmissionPortal,
  listIntelImageMatchByAttachment,
  postIntelMatchReport,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useImageMatch } from "@/contexts/ImageMatchContext";

interface MatchWithSubmission {
  match: IntelImageMatchModel;
  otherSubmission?: SubmissionPortal;
  otherAttachmentId: string;
}

interface AttachmentMatches {
  matches: IntelImageMatchModel[];
  /**
   * The requested attachment as it exists in the newest version of its submission.
   * Attachment ids are version-scoped, so an id captured before an edit no longer
   * appears in the submission we render. The API resolves it forward and rebases the
   * match ids to suit, so every id comparison below must use this, not the prop.
   */
  resolvedAttachmentId: string;
}

const matchCache = new Map<string, AttachmentMatches>();
const inflight = new Map<string, Promise<AttachmentMatches>>();

export function invalidateMatchCache() {
  matchCache.clear();
  inflight.clear();
}

function fetchMatchesCached(attachmentId: string): Promise<AttachmentMatches> {
  if (matchCache.has(attachmentId)) {
    return Promise.resolve(matchCache.get(attachmentId)!);
  }
  if (inflight.has(attachmentId)) {
    return inflight.get(attachmentId)!;
  }
  const promise = listIntelImageMatchByAttachment({
    path: { attachment_id: attachmentId },
  })
    .then((result) => {
      const resolved: AttachmentMatches = {
        matches: result.data?.matches ?? [],
        resolvedAttachmentId:
          result.data?.resolved_attachment_id ?? attachmentId,
      };
      matchCache.set(attachmentId, resolved);
      inflight.delete(attachmentId);
      return resolved;
    })
    .catch((err) => {
      inflight.delete(attachmentId);
      throw err;
    });
  inflight.set(attachmentId, promise);
  return promise;
}

const AttachmentMatchIndicator = ({
  attachmentId,
  submissionId,
}: {
  attachmentId: string;
  submissionId: string;
}) => {
  const [{ matches, resolvedAttachmentId }, setResult] =
    useState<AttachmentMatches>(
      () =>
        matchCache.get(attachmentId) ?? {
          matches: [],
          resolvedAttachmentId: attachmentId,
        },
    );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [enrichedMatches, setEnrichedMatches] = useState<MatchWithSubmission[]>(
    [],
  );
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [matchToDelete, setMatchToDelete] =
    useState<MatchWithSubmission | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation("customQuestions");
  const tm = (k: string, o?: Record<string, unknown>) =>
    t(`attachmentMatchIndicator.${k}`, o);
  const { navigateToReport, matchVersion, notifyMatchChange } = useImageMatch();
  const { orgId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (matchVersion > 0) {
      invalidateMatchCache();
    }
    let cancelled = false;
    fetchMatchesCached(attachmentId)
      .then((result) => {
        if (!cancelled) setResult(result);
      })
      .catch((err) => {
        console.error("Failed to fetch matches:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [attachmentId, matchVersion]);

  const handleOpen = async () => {
    setDialogOpen(true);
    setLoadingDetails(true);

    try {
      const enriched: MatchWithSubmission[] = await Promise.all(
        matches.map(async (match) => {
          const isOnSide1 = match.attachment_id_1 === resolvedAttachmentId;
          const otherSubmissionId = isOnSide1
            ? match.submission_id_2
            : match.submission_id_1;
          const otherAttachmentId = isOnSide1
            ? match.attachment_id_2
            : match.attachment_id_1;

          try {
            const result = await getSubmissionPortal({
              path: { submission_id: otherSubmissionId },
              query: { version: "latest" },
            });
            return {
              match,
              otherSubmission: result.data,
              otherAttachmentId,
            };
          } catch {
            return { match, otherAttachmentId };
          }
        }),
      );
      setEnrichedMatches(enriched);
    } catch (err) {
      console.error("Failed to load match details:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!matchToDelete) return;
    setDeleting(true);
    try {
      await deleteIntelImageMatch({
        path: { id: matchToDelete.match.id },
      });
      invalidateMatchCache();
      const remaining = matches.filter((m) => m.id !== matchToDelete.match.id);
      setResult({ matches: remaining, resolvedAttachmentId });
      setEnrichedMatches((prev) =>
        prev.filter((item) => item.match.id !== matchToDelete.match.id),
      );
      notifyMatchChange();
      toast.success(tm("imageMatchRemoved"));
      setMatchToDelete(null);
      if (remaining.length === 0) {
        setDialogOpen(false);
      }
    } catch (err) {
      console.error("Failed to delete image match:", err);
      toast.error(tm("removeMatchFailed"));
    } finally {
      setDeleting(false);
    }
  };

  if (matches.length === 0) return null;

  const getAttachmentUrl = (
    submission: SubmissionPortal | undefined,
    targetAttachmentId: string,
  ) => {
    if (!submission) return null;
    const attachment = submission.attachments.find(
      (a) => a.id === targetAttachmentId,
    );
    return attachment?.presigned_get?.s3_presigned?.uri ?? null;
  };

  return (
    <>
      <Chip
        icon={<CompareArrows sx={{ fontSize: 14 }} />}
        label={tm("matchCount", { count: matches.length })}
        size="small"
        onClick={handleOpen}
        sx={{
          backgroundColor: colors.primary.dark,
          color: colors.common.white,
          cursor: "pointer",
          fontSize: "12px",
          "& .MuiChip-icon": { color: colors.common.white },
          "&:hover": { backgroundColor: colors.info.dark },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
        >
          <CompareArrows />
          {tm("imageMatchesTitle", { count: matches.length })}
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="small"
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {enrichedMatches.map((item) => {
                const imageUrl = getAttachmentUrl(
                  item.otherSubmission,
                  item.otherAttachmentId,
                );
                const trackingNumber =
                  item.otherSubmission?.tracking_number ||
                  item.otherSubmission?.incident_number;
                const otherSubmissionId =
                  item.match.attachment_id_1 === resolvedAttachmentId
                    ? item.match.submission_id_2
                    : item.match.submission_id_1;
                const matchedAttachment =
                  item.otherSubmission?.attachments.find(
                    (a) => a.id === item.otherAttachmentId,
                  );

                return (
                  <Box
                    key={item.match.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 1.5,
                      border: "1px solid var(--divider)",
                      borderRadius: 1,
                      alignItems: "center",
                    }}
                  >
                    {imageUrl ? (
                      <Box
                        sx={{
                          width: 120,
                          height: 90,
                          flexShrink: 0,
                          borderRadius: 1,
                          overflow: "hidden",
                          backgroundColor: colors.action.hover,
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={
                            matchedAttachment?.filename || tm("matchedImageAlt")
                          }
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 120,
                          height: 90,
                          flexShrink: 0,
                          borderRadius: 1,
                          backgroundColor: colors.action.hover,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CompareArrows sx={{ color: colors.action.disabled }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {matchedAttachment?.filename || tm("unknownAttachment")}
                      </Typography>
                      {trackingNumber && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {tm("recordLabel", { trackingNumber })}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        {tm("matchedOn", {
                          date: new Date(
                            item.match.time_created,
                          ).toLocaleDateString(),
                        })}
                      </Typography>
                      {item.match.notes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mt: 0.25,
                            fontStyle: "italic",
                          }}
                        >
                          {item.match.notes}
                        </Typography>
                      )}
                    </Box>
                    {navigateToReport && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDialogOpen(false);
                          navigateToReport(otherSubmissionId);
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        <OpenInNew />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => setMatchToDelete(item)}
                      sx={{ flexShrink: 0 }}
                      aria-label={tm("removeMatchAria")}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                );
              })}
              {enrichedMatches.length > 0 && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={generating}
                    startIcon={
                      generating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Summarize />
                      )
                    }
                    onClick={async () => {
                      setGenerating(true);
                      try {
                        // Build unique submission+attachment items from matches
                        const itemsMap = new Map<
                          string,
                          {
                            submission_id: string;
                            attachment_id: string | null;
                            matched_on: string | null;
                          }
                        >();

                        // Include the source submission+attachment
                        itemsMap.set(
                          `${submissionId}:${resolvedAttachmentId}`,
                          {
                            submission_id: submissionId,
                            attachment_id: resolvedAttachmentId,
                            matched_on: null,
                          },
                        );

                        // Include each matched submission+attachment.
                        // Snapshot the match date so the report is preserved
                        // even if the image match is later deleted.
                        for (const item of enrichedMatches) {
                          const isOnSide1 =
                            item.match.attachment_id_1 === resolvedAttachmentId;
                          const otherSubId = isOnSide1
                            ? item.match.submission_id_2
                            : item.match.submission_id_1;
                          const otherAttId = isOnSide1
                            ? item.match.attachment_id_2
                            : item.match.attachment_id_1;
                          const key = `${otherSubId}:${otherAttId}`;
                          if (!itemsMap.has(key)) {
                            itemsMap.set(key, {
                              submission_id: otherSubId,
                              attachment_id: otherAttId,
                              matched_on: item.match.time_created,
                            });
                          }
                        }

                        const response = await postIntelMatchReport({
                          body: {
                            items: Array.from(itemsMap.values()),
                            notes: null,
                          },
                        });

                        toast.success(
                          tm("matchReportCreated", {
                            reference: response.data?.reference_number,
                          }),
                        );
                        setDialogOpen(false);
                        navigate(
                          `/rubiintel/${orgId}/match-report/${response.data?.id}/${resolvedAttachmentId}`,
                        );
                      } catch (err) {
                        console.error("Failed to create match report:", err);
                        toast.error(tm("createReportFailed"));
                      } finally {
                        setGenerating(false);
                      }
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    {generating ? tm("generating") : tm("generateMatchReport")}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={matchToDelete !== null}
        onClose={() => {
          if (!deleting) setMatchToDelete(null);
        }}
      >
        <DialogTitle>{tm("removeImageMatchTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tm("removeConfirm")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchToDelete(null)} disabled={deleting}>
            {tm("cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? tm("removing") : tm("removeMatch")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttachmentMatchIndicator;
